import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { frontendDb as db } from '../../lib/frontend-db';
import type { SlideItem } from '../../lib/frontend-db';

interface SortableSlideProps {
  slide: SlideItem;
  index: number;
  onEdit: (slide: SlideItem) => void;
  onDelete: (id: string) => void;
  isDragDisabled: boolean;
}

const SortableSlide: React.FC<SortableSlideProps> = ({ 
  slide, 
  index, 
  onEdit, 
  onDelete, 
  isDragDisabled 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: slide.id,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg p-4 shadow-sm ${
        isDragging ? 'shadow-lg opacity-50' : ''
      } transition-all duration-200`}
    >
      <div className="flex items-start gap-4">
        <div
          {...attributes}
          {...listeners}
          className={`text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1 ${
            isDragDisabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{ touchAction: 'none' }}
        >
          <GripVertical size={20} />
        </div>
        
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">
              Position {index + 1}: {slide.caption}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(slide)}
                className="p-1 text-green-700 hover:text-green-800"
                title="Bearbeiten"
              >
                <Pencil size={20} />
              </button>
              <button
                onClick={() => onDelete(slide.id)}
                className="p-1 text-red-600 hover:text-red-700"
                title="Löschen"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
          
          {slide.image_url && (
            <img
              src={slide.image_url}
              alt={slide.caption}
              className="w-full h-48 object-cover rounded"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const SlideshowPage = () => {
  const [slides, setSlides] = useState<SlideItem[]>([]);
  const [caption, setCaption] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [editingSlide, setEditingSlide] = useState<SlideItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDragDisabled, setIsDragDisabled] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      const slideItems = await db.getSlides();
      setSlides(slideItems);
    } catch (error) {
      console.error('Error loading slides:', error);
      setError('Fehler beim Laden der Slides');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let imageUrl = editingSlide?.image_url || '';

      if (selectedImage) {
        try {
          console.log('📤 Starting image upload for slide');
          imageUrl = await db.storeImage(selectedImage);
          console.log('✅ Image uploaded successfully:', imageUrl);
        } catch (imageError) {
          console.error('❌ Error uploading image:', imageError);
          setError('Fehler beim Hochladen des Bildes: ' + (imageError as Error).message);
          return;
        }
      }

      if (editingSlide) {
        try {
          await db.updateSlide(editingSlide.id, {
            ...editingSlide,
            caption,
            image_url: imageUrl,
          });
          console.log('✅ Slide updated successfully');
        } catch (updateError) {
          console.error('❌ Error updating slide:', updateError);
          setError('Fehler beim Aktualisieren des Slides: ' + (updateError as Error).message);
          return;
        }
      } else {
        try {
          await db.createSlide({
            caption,
            image_url: imageUrl,
            order_index: slides.length,
          });
          console.log('✅ Slide created successfully');
        } catch (createError) {
          console.error('❌ Error creating slide:', createError);
          setError('Fehler beim Erstellen des Slides: ' + (createError as Error).message);
          return;
        }
      }

      resetForm();
      window.location.reload();
    } catch (error) {
      console.error('❌ Unexpected error saving slide:', error);
      setError('Unerwarteter Fehler beim Speichern des Slides: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie diesen Slide wirklich löschen?')) {
      return;
    }

    try {
      await db.deleteSlide(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting slide:', error);
      setError('Fehler beim Löschen des Slides');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setIsDragDisabled(true);

    const oldIndex = slides.findIndex((slide) => slide.id === active.id);
    const newIndex = slides.findIndex((slide) => slide.id === over.id);

    const newSlides = arrayMove(slides, oldIndex, newIndex);
    
    // Update order_index for all items
    const updatedSlides = newSlides.map((slide, index) => ({
      ...slide,
      order_index: index
    }));

    // Optimistically update the UI
    setSlides(updatedSlides);

    try {
      // Update the order in the database
      for (const slide of updatedSlides) {
        await db.updateSlide(slide.id, { order_index: slide.order_index });
      }
    } catch (error) {
      console.error('Error updating slide order:', error);
      setError('Fehler beim Aktualisieren der Reihenfolge');
      // Reload original order on error
      await loadSlides();
    } finally {
      setIsDragDisabled(false);
    }
  };

  const handleEdit = (slide: SlideItem) => {
    setEditingSlide(slide);
    setCaption(slide.caption);
    setPreviewUrl(slide.image_url);
  };

  const resetForm = () => {
    setEditingSlide(null);
    setCaption('');
    setSelectedImage(null);
    setPreviewUrl('');
  };

  if (loading && slides.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse bg-white rounded-lg shadow-md p-6">
          <div className="text-center text-gray-500">Lädt...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          {editingSlide ? 'Slide bearbeiten' : 'Neuen Slide erstellen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Bildunterschrift</label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Bild</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
              required={!editingSlide?.image_url}
            />
            {(previewUrl || editingSlide?.image_url) && (
              <div className="mt-2">
                <img
                  src={previewUrl || editingSlide?.image_url}
                  alt="Vorschau"
                  className="w-full max-h-48 object-cover rounded"
                />
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Wird gespeichert...' : (editingSlide ? 'Aktualisieren' : 'Erstellen')}
            </button>
            {editingSlide && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Slides verwalten</h2>
        
        {slides.length > 0 ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Ziehen Sie die Slides am Griff-Symbol, um ihre Reihenfolge zu ändern.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={slides.map(slide => slide.id)} 
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4">
                  {slides.map((slide, index) => (
                    <SortableSlide
                      key={slide.id}
                      slide={slide}
                      index={index}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isDragDisabled={isDragDisabled}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        ) : (
          <div className="text-center text-gray-500 py-8">
            Keine Slides vorhanden. Erstellen Sie Ihren ersten Slide oben.
          </div>
        )}
      </div>
    </div>
  );
};

export default SlideshowPage;

